import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

interface ErpConnection {
  workEmail: string;
  fullName: string;
  employeeId: string;
  department?: string;
  designation?: string;
  connectedAt: string;
  lastSyncedAt?: string;
}

interface ErpState {
  connection: ErpConnection | null;
  isConnected: boolean;
  isConnecting: boolean;
  isChecking: boolean;

  checkConnection: () => Promise<void>;
  connectErp: (workEmail: string) => Promise<boolean>;
  disconnectErp: () => Promise<boolean>;
}

export const useErpStore = create<ErpState>((set, get) => ({
  connection: null,
  isConnected: false,
  isConnecting: false,
  isChecking: false,

  checkConnection: async () => {
    set({ isChecking: true });
    try {
      const res = await axiosInstance.get("/erp/status");

      if (res.data.connected) {
        set({
          isConnected: true,
          connection: res.data.connection,
        });
      } else {
        set({
          isConnected: false,
          connection: null,
        });
      }
    } catch (error: any) {
      console.error("Check connection error:", error);
      set({ isConnected: false, connection: null });
    } finally {
      set({ isChecking: false });
    }
  },

  connectErp: async (workEmail: string) => {
    set({ isConnecting: true });
    try {
      const res = await axiosInstance.post("/erp/connect", { workEmail });

      set({
        isConnected: true,
        connection: res.data.connection,
      });

      toast.success("ERP account connected successfully!");
      return true;
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to connect ERP account";
      toast.error(message);
      return false;
    } finally {
      set({ isConnecting: false });
    }
  },

  disconnectErp: async () => {
    try {
      await axiosInstance.post("/erp/disconnect");

      set({
        isConnected: false,
        connection: null,
      });

      toast.success("ERP account disconnected");
      return true;
    } catch (error: any) {
      toast.error("Failed to disconnect ERP account");
      return false;
    }
  },
}));
